import React from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box, Typography, Paper, Divider, Button, IconButton, TextField, Menu,
  Alert, Select, MenuItem, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Tooltip,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { BACKEND_URL } from '../utils/constants';
import { COLOR_PALETTE, getTagColor } from '../utils/tagColors';
import { colors } from '../theme';

import { ConstructionLoader } from './ConstructionLoader';
interface TagDef {
  id: string;
  name: string;
  color: string | null;
  sort_order: number;
  is_pipeline_stage: boolean;
  status: string;
}

// ───────────────────────────────────────────────────────────────────────────
// Color swatch grid, reused by the per-row picker and the "add" row.
// ───────────────────────────────────────────────────────────────────────────
function ColorSwatches({ selected, onPick }: { selected: string; onPick: (c: string) => void }) {
  return (
    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', p: 1, maxWidth: 220 }}>
      {COLOR_PALETTE.map((c) => (
        <Box
          key={c}
          onClick={() => onPick(c)}
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            backgroundColor: c,
            cursor: 'pointer',
            border: selected === c ? '3px solid' : '2px solid transparent',
            borderColor: selected === c ? colors.navy : 'transparent',
            outline: selected === c ? `2px solid ${c}` : 'none',
            outlineOffset: 1,
            transition: 'transform 100ms ease',
            '&:hover': { transform: 'scale(1.15)' },
          }}
        />
      ))}
    </Box>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// A single sortable tag row. Defined at module scope (NOT inside the parent)
// so its identity is stable across renders and inputs keep focus.
// ───────────────────────────────────────────────────────────────────────────
function SortableTagRow({
  item,
  badgeNumber,
  busy,
  isFirst,
  isLast,
  onMove,
  onRename,
  onRecolor,
  onChangeType,
  onArchive,
}: {
  item: TagDef;
  badgeNumber: number | null;
  busy: boolean;
  isFirst: boolean;
  isLast: boolean;
  onMove: (item: TagDef, direction: 'up' | 'down') => void;
  onRename: (id: string, name: string) => Promise<boolean>;
  onRecolor: (id: string, color: string) => void;
  onChangeType: (item: TagDef, isPipelineStage: boolean) => void;
  onArchive: (item: TagDef) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const [draft, setDraft] = React.useState(item.name);
  const [colorAnchor, setColorAnchor] = React.useState<null | HTMLElement>(null);

  React.useEffect(() => { setDraft(item.name); }, [item.name]);

  const color = getTagColor(item.name, item.color);

  const commitRename = async () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === item.name) {
      setDraft(item.name);
      return;
    }
    const ok = await onRename(item.id, trimmed);
    if (!ok) setDraft(item.name);
  };

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1,
        py: 1,
        px: { xs: 0.5, sm: 1 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: isDragging ? 'action.hover' : 'transparent',
        touchAction: 'none',
      }}
    >
      {/* Drag handle — sized for touch */}
      <Tooltip title="Drag to reorder">
        <IconButton
          {...attributes}
          {...listeners}
          size="small"
          aria-label="Drag to reorder"
          sx={{ cursor: 'grab', touchAction: 'none', p: 0.75, '&:active': { cursor: 'grabbing' } }}
        >
          <DragIndicatorIcon sx={{ fontSize: 26, color: 'text.disabled' }} />
        </IconButton>
      </Tooltip>

      {/* Keyboard / touch-accessible reorder fallback for the drag handle. */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
        <Tooltip title="Move up">
          <span>
            <IconButton
              size="small"
              aria-label={`Move ${item.name} up`}
              disabled={busy || isFirst}
              onClick={() => onMove(item, 'up')}
              sx={{ p: 0.25 }}
            >
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Move down">
          <span>
            <IconButton
              size="small"
              aria-label={`Move ${item.name} down`}
              disabled={busy || isLast}
              onClick={() => onMove(item, 'down')}
              sx={{ p: 0.25 }}
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Numbered/color badge — click to recolor */}
      <Tooltip title="Change color">
        <Box
          component="button"
          onClick={(e: React.MouseEvent<HTMLElement>) => setColorAnchor(e.currentTarget)}
          aria-label="Change color"
          sx={{
            width: 34,
            height: 34,
            minWidth: 34,
            borderRadius: '50%',
            backgroundColor: color,
            color: '#fff',
            border: '2px solid #fff',
            boxShadow: '0 0 0 1px rgba(0,0,0,0.15)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '0.85rem',
            p: 0,
          }}
        >
          {badgeNumber ?? ''}
        </Box>
      </Tooltip>
      <Menu anchorEl={colorAnchor} open={Boolean(colorAnchor)} onClose={() => setColorAnchor(null)}>
        <ColorSwatches
          selected={item.color || color}
          onPick={(c) => { onRecolor(item.id, c); setColorAnchor(null); }}
        />
      </Menu>

      {/* Inline rename */}
      <TextField
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitRename}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { (e.target as HTMLInputElement).blur(); }
          if (e.key === 'Escape') { setDraft(item.name); (e.target as HTMLInputElement).blur(); }
        }}
        variant="standard"
        size="small"
        disabled={busy}
        sx={{ flex: '1 1 140px', minWidth: 120 }}
        inputProps={{ 'aria-label': `Rename ${item.name}` }}
      />

      {/* Type toggle: Pipeline stage vs Label */}
      <Select
        value={item.is_pipeline_stage ? 'stage' : 'label'}
        onChange={(e) => onChangeType(item, e.target.value === 'stage')}
        size="small"
        disabled={busy}
        sx={{ flex: '0 0 auto', minWidth: 130, fontSize: '0.85rem' }}
      >
        <MenuItem value="stage">Pipeline stage</MenuItem>
        <MenuItem value="label">Label</MenuItem>
      </Select>

      {/* Archive */}
      <Tooltip title="Archive">
        <IconButton
          size="small"
          aria-label={`Archive ${item.name}`}
          disabled={busy}
          onClick={() => onArchive(item)}
        >
          <ArchiveOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

const PipelineTagSettings: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [items, setItems] = React.useState<TagDef[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string>('');
  const [busy, setBusy] = React.useState(false);

  // "Add tag" row state
  const [newName, setNewName] = React.useState('');
  const [newColor, setNewColor] = React.useState<string>(COLOR_PALETTE[0]);
  const [newIsStage, setNewIsStage] = React.useState(true);
  const [addColorAnchor, setAddColorAnchor] = React.useState<null | HTMLElement>(null);

  // Archive confirmation
  const [archiveTarget, setArchiveTarget] = React.useState<TagDef | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const authedFetch = React.useCallback(async (path: string, init?: RequestInit) => {
    const token = await getAccessTokenSilently();
    return fetch(`${BACKEND_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(init?.headers || {}),
      },
      credentials: 'include',
    });
  }, [getAccessTokenSilently]);

  const loadDefs = React.useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const r = await authedFetch('/api/tag_definitions', { method: 'GET' });
      if (!r.ok) throw new Error('Failed to load tags');
      const data: TagDef[] = await r.json();
      // Group stages first, then labels — each preserving sort_order — so the
      // numbered pipeline always renders above the unnumbered labels.
      const stages = data.filter((d) => d.is_pipeline_stage);
      const labels = data.filter((d) => !d.is_pipeline_stage);
      setItems([...stages, ...labels]);
    } catch (e: any) {
      setError(e?.message || 'Failed to load tags');
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  React.useEffect(() => { loadDefs(); }, [loadDefs]);

  const stages = React.useMemo(() => items.filter((i) => i.is_pipeline_stage), [items]);
  const labels = React.useMemo(() => items.filter((i) => !i.is_pipeline_stage), [items]);

  // Persist the current global order (stages first, then labels).
  const persistOrder = React.useCallback(async (next: TagDef[]): Promise<boolean> => {
    try {
      const r = await authedFetch('/api/tag_definitions/reorder', {
        method: 'PUT',
        body: JSON.stringify({ ordered_ids: next.map((i) => i.id) }),
      });
      return r.ok;
    } catch {
      return false;
    }
  }, [authedFetch]);

  const handleDragEnd = async (group: 'stage' | 'label', event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const list = group === 'stage' ? stages : labels;
    const oldIndex = list.findIndex((i) => i.id === active.id);
    const newIndex = list.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const prev = items;
    const reordered = arrayMove(list, oldIndex, newIndex);
    const next = group === 'stage' ? [...reordered, ...labels] : [...stages, ...reordered];
    setItems(next); // optimistic

    const ok = await persistOrder(next);
    if (!ok) {
      setItems(prev); // rollback
      setError('Could not save the new order. Your change was reverted.');
    } else {
      setError('');
    }
  };

  // Keyboard/click reorder fallback (move-up / move-down buttons). Mirrors
  // handleDragEnd: reorders within the item's own group and persists.
  const handleMove = async (item: TagDef, direction: 'up' | 'down') => {
    const group: 'stage' | 'label' = item.is_pipeline_stage ? 'stage' : 'label';
    const list = group === 'stage' ? stages : labels;
    const idx = list.findIndex((i) => i.id === item.id);
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (idx < 0 || target < 0 || target >= list.length) return;

    const prev = items;
    const reordered = arrayMove(list, idx, target);
    const next = group === 'stage' ? [...reordered, ...labels] : [...stages, ...reordered];
    setItems(next); // optimistic

    const ok = await persistOrder(next);
    if (!ok) {
      setItems(prev); // rollback
      setError('Could not save the new order. Your change was reverted.');
    } else {
      setError('');
    }
  };

  const handleRename = async (id: string, name: string): Promise<boolean> => {
    try {
      const r = await authedFetch(`/api/tag_definitions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name }),
      });
      if (r.status === 409) {
        setError(`A tag named "${name}" already exists.`);
        return false;
      }
      if (!r.ok) throw new Error('rename failed');
      const updated: TagDef = await r.json();
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, name: updated.name } : i)));
      setError('');
      return true;
    } catch {
      setError('Could not rename the tag.');
      return false;
    }
  };

  const handleRecolor = async (id: string, color: string) => {
    const prev = items;
    setItems((p) => p.map((i) => (i.id === id ? { ...i, color } : i))); // optimistic
    try {
      const r = await authedFetch(`/api/tag_definitions/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ color }),
      });
      if (!r.ok) throw new Error('recolor failed');
      setError('');
    } catch {
      setItems(prev);
      setError('Could not update the color.');
    }
  };

  const handleChangeType = async (item: TagDef, isPipelineStage: boolean) => {
    if (item.is_pipeline_stage === isPipelineStage) return;
    const prev = items;
    // Move the item to the end of its destination group, keeping stages first.
    const remaining = items.filter((i) => i.id !== item.id);
    const moved = { ...item, is_pipeline_stage: isPipelineStage };
    const newStages = remaining.filter((i) => i.is_pipeline_stage);
    const newLabels = remaining.filter((i) => !i.is_pipeline_stage);
    if (isPipelineStage) newStages.push(moved); else newLabels.push(moved);
    const next = [...newStages, ...newLabels];
    setItems(next); // optimistic
    setBusy(true);
    try {
      const r = await authedFetch(`/api/tag_definitions/${item.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ is_pipeline_stage: isPipelineStage }),
      });
      if (!r.ok) throw new Error('type change failed');
      // Persist the new ordering so stages-first grouping survives a refresh.
      await persistOrder(next);
      setError('');
    } catch {
      setItems(prev);
      setError('Could not change the tag type.');
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      const r = await authedFetch('/api/tag_definitions', {
        method: 'POST',
        body: JSON.stringify({ name, color: newColor, is_pipeline_stage: newIsStage }),
      });
      if (r.status === 409) {
        setError(`A tag named "${name}" already exists.`);
        return;
      }
      if (!r.ok) throw new Error('add failed');
      const created: TagDef = await r.json();
      // Place at the end of its group and persist grouped ordering.
      const newStages = items.filter((i) => i.is_pipeline_stage);
      const newLabels = items.filter((i) => !i.is_pipeline_stage);
      if (created.is_pipeline_stage) newStages.push(created); else newLabels.push(created);
      const next = [...newStages, ...newLabels];
      setItems(next);
      await persistOrder(next);
      setNewName('');
      setNewColor(COLOR_PALETTE[0]);
      setNewIsStage(true);
      setError('');
    } catch {
      setError('Could not add the tag.');
    } finally {
      setBusy(false);
    }
  };

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    const target = archiveTarget;
    const prev = items;
    setItems((p) => p.filter((i) => i.id !== target.id)); // optimistic
    setArchiveTarget(null);
    try {
      const r = await authedFetch(`/api/tag_definitions/${target.id}/remove`, { method: 'PATCH' });
      if (!r.ok) throw new Error('archive failed');
      setError('');
    } catch {
      setItems(prev);
      setError('Could not archive the tag.');
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 4 }}>
      <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
        Pipeline
      </Typography>
      <Divider sx={{ mb: 2 }} />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define the tags you use to track deals. Drag to set the order of your
        pipeline stages; tags marked as <strong>Labels</strong> are listed below
        and stay unnumbered. Changes here apply everywhere you tag deals.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <ConstructionLoader size={48} />
        </Box>
      ) : (
        <>
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              You don't have any tags yet. Add your first pipeline stage below.
            </Typography>
          ) : (
            <Box sx={{ maxWidth: 680 }}>
              {/* Pipeline stages — numbered, sortable */}
              {stages.length > 0 && (
                <>
                  <Typography variant="overline" color="text.secondary">Pipeline stages</Typography>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => handleDragEnd('stage', e)}
                  >
                    <SortableContext items={stages.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                      {stages.map((item, idx) => (
                        <SortableTagRow
                          key={item.id}
                          item={item}
                          badgeNumber={idx + 1}
                          busy={busy}
                          isFirst={idx === 0}
                          isLast={idx === stages.length - 1}
                          onMove={handleMove}
                          onRename={handleRename}
                          onRecolor={handleRecolor}
                          onChangeType={handleChangeType}
                          onArchive={setArchiveTarget}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </>
              )}

              {/* Labels — unnumbered, sortable among themselves */}
              {labels.length > 0 && (
                <Box sx={{ mt: stages.length > 0 ? 3 : 0 }}>
                  <Typography variant="overline" color="text.secondary">Labels</Typography>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(e) => handleDragEnd('label', e)}
                  >
                    <SortableContext items={labels.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                      {labels.map((item, idx) => (
                        <SortableTagRow
                          key={item.id}
                          item={item}
                          badgeNumber={null}
                          busy={busy}
                          isFirst={idx === 0}
                          isLast={idx === labels.length - 1}
                          onMove={handleMove}
                          onRename={handleRename}
                          onRecolor={handleRecolor}
                          onChangeType={handleChangeType}
                          onArchive={setArchiveTarget}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </Box>
              )}
            </Box>
          )}

          {/* Add tag row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mt: 3,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              maxWidth: 680,
            }}
          >
            <Tooltip title="Choose color">
              <Box
                component="button"
                onClick={(e: React.MouseEvent<HTMLElement>) => setAddColorAnchor(e.currentTarget)}
                aria-label="Choose color for new tag"
                sx={{
                  width: 34, height: 34, minWidth: 34, borderRadius: '50%',
                  backgroundColor: newColor, border: '2px solid #fff',
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.15)', cursor: 'pointer', p: 0,
                }}
              />
            </Tooltip>
            <Menu anchorEl={addColorAnchor} open={Boolean(addColorAnchor)} onClose={() => setAddColorAnchor(null)}>
              <ColorSwatches selected={newColor} onPick={(c) => { setNewColor(c); setAddColorAnchor(null); }} />
            </Menu>

            <TextField
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
              placeholder="New tag name"
              size="small"
              disabled={busy}
              sx={{ flex: '1 1 140px', minWidth: 120 }}
              inputProps={{ 'aria-label': 'New tag name' }}
            />

            <Select
              value={newIsStage ? 'stage' : 'label'}
              onChange={(e) => setNewIsStage(e.target.value === 'stage')}
              size="small"
              disabled={busy}
              sx={{ flex: '0 0 auto', minWidth: 130, fontSize: '0.85rem' }}
            >
              <MenuItem value="stage">Pipeline stage</MenuItem>
              <MenuItem value="label">Label</MenuItem>
            </Select>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={busy || !newName.trim()}
            >
              Add tag
            </Button>
          </Box>
        </>
      )}

      {/* Archive confirmation */}
      <Dialog open={Boolean(archiveTarget)} onClose={() => setArchiveTarget(null)}>
        <DialogTitle>Archive "{archiveTarget?.name}"?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This tag will stay on any deals that already have it, but it will no
            longer appear in the tag pickers when tagging deals. You can always
            re-create it later.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArchiveTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmArchive}>Archive</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default PipelineTagSettings;
